import { MonitorUp, RefreshCw, Upload } from 'lucide-react';
import { StatusIcone } from '@/components/patterns/status-badge';
import type { EventoBuild } from '@/data/types';
import { ROTULO_STATUS } from '@/lib/capabilities';
import { formatarDataHora, formatarRelativo } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Texto de cada tipo de evento — o que aconteceu, em uma frase. */
function descrever(evento: EventoBuild): string {
  switch (evento.tipo) {
    case 'publicado':
      return 'Publicada pelo Jenkins';
    case 'republicado':
      return 'Republicada pelo Jenkins (links e manifesto substituídos)';
    case 'painel':
      return 'Publicada no painel do cliente';
    case 'status':
    default: {
      const de = evento.status_anterior ? ROTULO_STATUS[evento.status_anterior] : null;
      const para = evento.status_novo ? ROTULO_STATUS[evento.status_novo] : 'status desconhecido';
      return de ? `Status: ${de} → ${para}` : `Status: ${para}`;
    }
  }
}

function Icone({ evento }: { evento: EventoBuild }) {
  if (evento.tipo === 'publicado') return <Upload aria-hidden="true" />;
  if (evento.tipo === 'republicado') return <RefreshCw aria-hidden="true" />;
  if (evento.tipo === 'painel') return <MonitorUp aria-hidden="true" />;
  return evento.status_novo ? <StatusIcone status={evento.status_novo} /> : <RefreshCw aria-hidden="true" />;
}

const COR_STATUS: Record<string, string> = {
  homologado: 'text-success border-success-border bg-success-wash',
  nao_homologado: 'text-on-surface-muted border-border bg-bg-subtle',
  reprovado: 'text-danger border-danger-border bg-danger-wash',
};

/** Linha do tempo de eventos da versão, mais recente primeiro. */
export function LinhaDoTempo({ eventos }: { eventos: EventoBuild[] }) {
  if (eventos.length === 0) {
    return <p className="text-body text-on-surface-muted">Nenhum evento registrado para esta versão.</p>;
  }
  return (
    <ol className="flex flex-col" aria-label="Linha do tempo da versão">
      {eventos.map((evento, indice) => (
        <li key={evento.id} className="relative flex gap-3 pb-5 last:pb-0">
          {indice < eventos.length - 1 ? (
            <span className="absolute left-[15px] top-8 h-[calc(100%-2rem)] w-px bg-border" aria-hidden="true" />
          ) : null}
          <span
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-full border [&_svg]:size-4',
              evento.tipo === 'painel'
                ? 'border-primary-border bg-primary-wash text-primary'
                : evento.tipo === 'status' && evento.status_novo && COR_STATUS[evento.status_novo]
                  ? COR_STATUS[evento.status_novo]
                  : 'border-border bg-bg-subtle text-icon',
            )}
          >
            <Icone evento={evento} />
          </span>
          <div className="flex min-w-0 flex-col gap-0.5 pt-1">
            <p className="text-body font-bold">{descrever(evento)}</p>
            {evento.motivo ? <p className="text-body text-on-surface">“{evento.motivo}”</p> : null}
            <p className="text-cell text-on-surface-muted">
              {evento.usuario} ·{' '}
              <time dateTime={evento.criado_em} title={formatarDataHora(evento.criado_em)}>
                {formatarRelativo(evento.criado_em)}
              </time>
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}
